/**
 * 动效层：Context + `.fx-layer` 宿主（§11.6）。
 *
 * `.fx-layer` 是唯一放筹码精灵的地方：`contain: strict` + `pointer-events: none`，
 * 于是精灵飞来飞去既不会触发外部重排，也不会挡住座位/按钮的点击。
 *
 * 组件不直接 new FxController：拿不到布局缓存的实例会静默地什么都不播，
 * 排查起来非常痛苦。统一走 Provider，拿不到就是 null，`useFxNode` 自己降级。
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode, RefObject } from 'react'

import { useSettingsStore } from '../store/index.js'
import type { TableLayout } from '../table/seatGeometry.js'

import { FxController } from './FxController.js'
import { detectLowEndDevice, FULL_MOTION, type MotionProfile } from './motion.js'

import './fx.css'

const FxContext = createContext<FxController | null>(null)

export function useFx(): FxController | null {
  return useContext(FxContext)
}

/**
 * 把一个 DOM 节点登记给动效层。用 ref 回调而不是 useEffect：
 * 节点卸载时 React 会用 `null` 再调一次，注销时机因此恰好是节点真正消失的那一刻。
 */
export function useFxNode(key: string): (el: HTMLElement | null) => void {
  const fx = useFx()
  const prevRef = useRef<{ key: string; el: HTMLElement } | null>(null)
  // useCallback 不是优化而是正确性：ref 回调换身份会让 React 每次渲染都
  // 先用 null 再用节点调一遍，注册表因此每帧抖动一次。
  return useCallback((el: HTMLElement | null): void => {
    const prev = prevRef.current
    if (prev !== null && (el === null || prev.key !== key)) {
      fx?.unregister(prev.key, prev.el)
      prevRef.current = null
    }
    if (el !== null && fx !== null) {
      fx.register(key, el)
      prevRef.current = { key, el }
    }
  }, [fx, key])
}

/**
 * 当前运动档位。三个来源：
 *  - `prefers-reduced-motion`（订阅，随系统设置实时变）
 *  - 设置面板的「省电模式」
 *  - `useDevicePerf` 的静态探测（核心数/内存）
 *
 * ★ 必须被 FxController 读取（§11.6 末尾）：WAAPI 的 duration 是 JS 常量，
 * 只改 CSS 变量对飞牌/推池完全无效。
 */
export function useMotionProfile(): MotionProfile {
  const liteSetting = useSettingsStore((s) => s.lite)
  const [reduced, setReduced] = useState<boolean>(false)
  const [lowEnd, setLowEnd] = useState<boolean>(false)

  useEffect(() => {
    setLowEnd(detectLowEndDevice())
    const mq = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (mq === undefined) return undefined
    setReduced(mq.matches)
    const onChange = (e: MediaQueryListEvent): void => setReduced(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return useMemo(
    () => ({ reduced, lite: liteSetting || lowEnd }),
    [reduced, liteSetting, lowEnd],
  )
}

export interface FxProviderProps {
  /** useTableGeometry 的几何缓存。动画只读它（红线 #47）。 */
  readonly layoutRef: RefObject<TableLayout | null>
  readonly children: ReactNode
}

export function FxProvider({ layoutRef, children }: FxProviderProps): ReactNode {
  const profile = useMotionProfile()
  const profileRef = useRef<MotionProfile>(profile)
  profileRef.current = profile

  // controller 的身份必须在整个牌桌生命周期内稳定：换一个实例 = 丢掉所有已注册节点
  const [controller] = useState(
    () =>
      new FxController({
        getLayout: () => layoutRef.current,
        getProfile: () => profileRef.current ?? FULL_MOTION,
      }),
  )

  useEffect(() => {
    return () => controller.cancelAll()
  }, [controller])

  return <FxContext.Provider value={controller}>{children}</FxContext.Provider>
}

/** 精灵宿主。放在舞台内、座位之上、dock 之下（--z-fx）。 */
export function FxLayer(): ReactNode {
  const fx = useFx()
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    fx?.setLayer(ref.current)
    return () => fx?.setLayer(null)
  }, [fx])

  return <div className="fx-layer" ref={ref} aria-hidden="true" />
}
